import { useCallback, useState } from 'react';
import {
	openPopup,
	pollPopup,
	generateState,
	buildAuthUrl,
} from './OAuthPopup';

const MICROSOFT_AUTH_URL =
	'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';

/**
 * LoginSocialMicrosoft Component
 *
 * Custom Microsoft OAuth component (Authorization Code flow)
 * Returns authorization CODE (not access token) for secure backend exchange
 *
 * @param {Object} props
 * @param {string} props.client_id - Microsoft App ID
 * @param {string} props.redirect_uri - Redirect URI (must match Azure Portal)
 * @param {string} props.scope - OAuth scopes (default: 'openid profile email User.Read')
 * @param {string} props.tenant - Tenant ID (default: 'common')
 * @param {Function} props.onResolve - Success callback ({ provider, data })
 * @param {Function} props.onReject - Error callback
 * @param {boolean} props.isOnlyGetCode - Always true (we only get code, not token)
 * @param {boolean} props.isDisabled - Disable button
 * @param {ReactNode} props.children - Button/trigger element
 */
export const LoginSocialMicrosoft = ({
	client_id,
	redirect_uri,
	scope = 'openid profile email User.Read',
	tenant = 'common',
	onResolve,
	onReject,
	isOnlyGetCode = true, // Always get code (not token)
	isDisabled = false,
	children,
}) => {
	const [isLoading, setIsLoading] = useState(false);

	/**
	 * Handle OAuth login click
	 */
	const handleLogin = useCallback(() => {
		if (isDisabled || isLoading) return;

		setIsLoading(true);

		// Generate CSRF state
		const state = generateState();
		localStorage.setItem('oauth_microsoft_state', state);

		// Build authorization URL
		const authUrl = buildAuthUrl(
			MICROSOFT_AUTH_URL.replace('/common/', `/${tenant}/`),
			{
				client_id,
				redirect_uri,
				response_type: 'code', // Authorization code flow
				scope,
				state,
				response_mode: 'query', // Return params in query string
				prompt: 'select_account', // Always show account selector
			},
		);

		// Open popup
		const popup = openPopup(authUrl, 'Microsoft Login');

		if (!popup) {
			setIsLoading(false);
			onReject && onReject(new Error('Popup blocked. Please allow popups.'));
			return;
		}

		// Poll popup for callback
		pollPopup(
			popup,
			redirect_uri,
			// Success callback
			({ code, state: returnedState }) => {
				setIsLoading(false);

				// Verify state (CSRF protection)
				const savedState = localStorage.getItem('oauth_microsoft_state');
				localStorage.removeItem('oauth_microsoft_state');

				if (savedState !== returnedState) {
					onReject &&
						onReject(new Error('State mismatch - possible CSRF attack'));
					return;
				}

				// Return code to parent
				onResolve &&
					onResolve({
						provider: 'microsoft',
						data: {
							code,
							redirect_uri, // Backend needs this to exchange code
						},
					});
			},
			// Error callback
			(error) => {
				setIsLoading(false);
				localStorage.removeItem('oauth_microsoft_state');
				onReject && onReject(error);
			},
		);
	}, [
		client_id,
		redirect_uri,
		scope,
		tenant,
		onResolve,
		onReject,
		isDisabled,
		isLoading,
	]);

	// Render children with onClick handler
	return (
		<div
			onClick={handleLogin}
			style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
		>
			{children}
		</div>
	);
};
